import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Layout from "@/components/Layout";
import { Link } from "react-router-dom";
import {
  Download, RefreshCw, FileText, Filter, TrendingUp, Receipt, Users as UsersIcon, Truck, CheckCircle2, Circle, CircleDollarSign,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "@/lib/utils";

const monthsAround = (n = 12) => {
  const arr = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0");
    arr.push(`${y}-${m}`);
    d.setMonth(d.getMonth() - 1);
  }
  return arr;
};

const chf = (v) => {
  const n = Number(v || 0);
  return `CHF ${n.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const INVOICE_STATUS_MAP = {
  open:     { label: "Offen",      cls: "border-[var(--border-strong)] bg-[var(--surface-2)] text-[var(--text-2)]" },
  invoiced: { label: "Fakturiert", cls: "border-[#D4AF37]/50 bg-[#D4AF37]/10 text-[#7A5A0E]" },
  paid:     { label: "Bezahlt",    cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-800" },
};

function InvoiceStatusBadge({ s }) {
  const cfg = INVOICE_STATUS_MAP[s || "open"];
  return <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${cfg.cls}`}>{cfg.label}</span>;
}

export default function Billing() {
  const [tab, setTab] = useState("customer"); // "customer" | "driver"
  const [customers, setCustomers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [data, setData] = useState({ orders: [], summary: { total_revenue: 0, order_count: 0, by_customer: [], by_month: [] } });
  const [driverData, setDriverData] = useState({ rows: [], summary: { by_driver: [], grand_total: 0, order_count: 0 } });
  const [loading, setLoading] = useState(true);
  const [recalcing, setRecalcing] = useState(false);
  const [filters, setFilters] = useState({
    customer_id: "",
    month: new Date().toISOString().slice(0, 7), // current YYYY-MM
    status: "",
  });
  const [error, setError] = useState("");

  const months = useMemo(() => monthsAround(18), []);

  const fetchData = async (opts = {}) => {
    setLoading(true); setError("");
    try {
      const params = {};
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.month) params.month = filters.month;
      if (filters.status) params.status = filters.status;
      if (filters.invoice_status) params.invoice_status = filters.invoice_status;
      if (opts.recalc) params.recalc = true;
      const { data } = await api.get("/admin/billing", { params });
      setData(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); setRecalcing(false); }
  };

  useEffect(() => {
    api.get("/customers").then(r => setCustomers(r.data || []));
    api.get("/drivers").then(r => setDrivers(r.data || []));
  }, []);

  // Fetch driver payouts whenever driver tab is active or filters change
  useEffect(() => {
    if (tab !== "driver") return;
    (async () => {
      try {
        const params = {};
        if (filters.month) params.month = filters.month;
        if (filters.payout_status) params.payout_status = filters.payout_status;
        const { data } = await api.get("/admin/billing/drivers", { params });
        setDriverData(data);
      } catch (e) { setError(formatApiError(e)); }
    })();
  }, [tab, filters.month, filters.payout_status]);

  useEffect(() => { fetchData(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCsv = async () => {
    const params = new URLSearchParams();
    if (filters.customer_id) params.set("customer_id", filters.customer_id);
    if (filters.month) params.set("month", filters.month);
    if (filters.status) params.set("status", filters.status);
    const token = localStorage.getItem("nv_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/billing/export.csv?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `abrechnung_${filters.month || "alle"}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadCollective = async () => {
    if (!filters.customer_id) {
      alert("Bitte zuerst einen Kunden im Filter wählen, um eine Sammelrechnung zu erstellen.");
      return;
    }
    const params = new URLSearchParams();
    if (filters.month) params.set("month", filters.month);
    const token = localStorage.getItem("nv_token");
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/admin/billing/collective-invoice/${filters.customer_id}?${params}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const downloadSingle = async (orderId, orderNo) => {
    const token = localStorage.getItem("nv_token");
    const res = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/admin/billing/invoice/${orderId}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    const blob = await res.blob();
    window.open(URL.createObjectURL(blob), "_blank");
  };

  const exportDriverCsv = async () => {
    const params = new URLSearchParams();
    if (filters.month) params.set("month", filters.month);
    const token = localStorage.getItem("nv_token");
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/billing/drivers.csv?${params}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `fahrer-abrechnung_${filters.month || "alle"}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  // Cycle invoice/payout status: open -> invoiced -> paid -> open
  const nextInvoiceStatus = (s) => (s === "open" ? "invoiced" : s === "invoiced" ? "paid" : "open");
  const setInvoiceStatus = async (orderId, newStatus) => {
    try {
      await api.patch(`/admin/billing/orders/${orderId}/invoice-status`, { status: newStatus });
      toast.success(newStatus === "invoiced" ? "Als fakturiert markiert" : newStatus === "paid" ? "Als bezahlt markiert" : "Status zurückgesetzt");
      fetchData();
    } catch (e) {
      toast.error("Speichern fehlgeschlagen", { description: formatApiError(e) });
    }
  };
  const setPayoutStatus = async (orderId, newStatus) => {
    try {
      await api.patch(`/admin/billing/orders/${orderId}/payout-status`, { status: newStatus });
      toast.success(newStatus === "invoiced" ? "Fahrer-Auszahlung fakturiert" : newStatus === "paid" ? "Fahrer-Auszahlung bezahlt" : "Status zurückgesetzt");
      // refetch driver data
      const params = {};
      if (filters.month) params.month = filters.month;
      if (filters.payout_status) params.payout_status = filters.payout_status;
      const { data } = await api.get("/admin/billing/drivers", { params });
      setDriverData(data);
    } catch (e) {
      toast.error("Speichern fehlgeschlagen", { description: formatApiError(e) });
    }
  };

  const StatusBadge = InvoiceStatusBadge;

  const monthMax = useMemo(() => Math.max(1, ...data.summary.by_month.map(m => m.total)), [data]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10 space-y-6">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-3">
          <div>
            <div className="label-eyebrow" style={{ color: "var(--gold)" }}>Abrechnung</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">Umsatz & Rechnungen</h1>
            <p className="text-sm text-[var(--text-2)] mt-1">Auswertung nach Kunde, Monat und Status · CSV-Export & Sammelrechnungen.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="billing-recalc"
              onClick={() => { setRecalcing(true); fetchData({ recalc: true }); }}
              className="btn-ghost px-3 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2"
            >
              <RefreshCw size={14} className={recalcing ? "animate-spin" : ""} /> Neu berechnen
            </button>
            <button
              data-testid="billing-export-csv"
              onClick={exportCsv}
              className="btn-ghost px-3 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2"
            >
              <Download size={14} /> CSV
            </button>
            <button
              data-testid="billing-collective-pdf"
              onClick={downloadCollective}
              disabled={!filters.customer_id}
              className="btn-gold px-3 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2 disabled:opacity-40"
            >
              <FileText size={14} /> Sammelrechnung
            </button>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-[var(--border)]">
          {[
            { key: "customer", label: "Kunden-Umsatz", icon: Receipt },
            { key: "driver", label: "Fahrer-Abrechnung", icon: Truck },
          ].map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`billing-tab-${t.key}`}
              className={`px-4 py-2.5 text-xs uppercase tracking-wider flex items-center gap-2 border-b-2 -mb-px transition-colors ${
                tab === t.key
                  ? "border-[#D4AF37] text-[var(--text)]"
                  : "border-transparent text-[var(--text-3)] hover:text-[var(--text-2)]"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "driver" && (
          <div className="space-y-4" data-testid="billing-driver-panel">
            <div className="card p-4 flex flex-wrap items-end gap-3">
              <div className="min-w-[140px]">
                <label className="label-eyebrow block mb-2">Monat</label>
                <select className="input-base" value={filters.month} onChange={e => setFilters({ ...filters, month: e.target.value })} data-testid="driver-billing-month">
                  <option value="">Alle</option>
                  {months.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <div className="min-w-[160px]">
                <label className="label-eyebrow block mb-2 flex items-center gap-2"><Receipt size={11} /> Auszahlungsstatus</label>
                <select className="input-base" value={filters.payout_status} onChange={e => setFilters({ ...filters, payout_status: e.target.value })} data-testid="driver-billing-payout-status">
                  <option value="">Alle</option>
                  <option value="open">Offen</option>
                  <option value="invoiced">Fakturiert</option>
                  <option value="paid">Bezahlt</option>
                </select>
              </div>
              <button onClick={exportDriverCsv} data-testid="driver-billing-export" className="btn-ghost px-3 py-2 text-xs uppercase tracking-wider rounded-sm flex items-center gap-2"><Download size={14} /> CSV</button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div className="card p-5">
                <div className="text-xs uppercase tracking-wider text-[var(--text-3)] flex items-center gap-2"><Truck size={12} /> Pauschalsumme</div>
                <div className="text-2xl md:text-3xl font-semibold mt-2" data-testid="driver-billing-total">{chf(driverData.summary.grand_total)}</div>
              </div>
              <div className="card p-5">
                <div className="text-xs uppercase tracking-wider text-[var(--text-3)]">Aufträge</div>
                <div className="text-2xl md:text-3xl font-semibold mt-2">{driverData.summary.order_count}</div>
              </div>
              <div className="card p-5">
                <div className="text-xs uppercase tracking-wider text-[var(--text-3)]">Top Fahrer</div>
                <div className="text-sm font-semibold mt-2 truncate">{driverData.summary.by_driver[0]?.driver_name || "—"}</div>
                <div className="text-xs text-[var(--text-2)] mt-1">{driverData.summary.by_driver[0] ? chf(driverData.summary.by_driver[0].total) : ""}</div>
              </div>
            </div>
            <div className="card p-5">
              <div className="label-eyebrow mb-3">Summe pro Fahrer</div>
              {driverData.summary.by_driver.length === 0 ? (
                <div className="text-sm text-[var(--text-3)] py-4 text-center">Keine Pauschalen erfasst im gewählten Zeitraum.</div>
              ) : (
                <div className="space-y-1">
                  {driverData.summary.by_driver.map(d => (
                    <div key={d.driver_id} className="flex items-center justify-between py-2 border-b border-[var(--border)] last:border-0">
                      <div className="flex items-center gap-3"><Truck size={14} className="text-[var(--text-3)]" /><div className="font-medium">{d.driver_name}</div><div className="text-xs text-[var(--text-3)]">{d.order_count} Auftrag{d.order_count !== 1 ? "räge" : ""}</div></div>
                      <div className="font-mono font-semibold" style={{ color: "var(--header)" }}>{chf(d.total)}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="card overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead>
                  <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                    <th className="text-left py-3 px-4">Auftrag</th>
                    <th className="text-left py-3 px-4">Datum</th>
                    <th className="text-left py-3 px-4">Fahrer</th>
                    <th className="text-left py-3 px-4">Kunde</th>
                    <th className="text-left py-3 px-4">Strecke</th>
                    <th className="text-right py-3 px-4">CHF</th>
                    <th className="text-left py-3 px-4">Auszahlung</th>
                    <th className="text-right py-3 px-4">Aktion</th>
                  </tr>
                </thead>
                <tbody data-testid="driver-billing-table">
                  {driverData.rows.length === 0 && (
                    <tr><td colSpan={8} className="text-center py-10 text-[var(--text-3)]">Keine Pauschalen erfasst.</td></tr>
                  )}
                  {driverData.rows.map(r => (
                    <tr key={r.id} className="border-b border-[var(--border)]" data-testid={`driver-billing-row-${r.id}`}>
                      <td className="py-3 px-4 font-mono"><Link to={`/admin/orders/${r.id}`} className="hover:text-[#D4AF37]">{r.order_number}</Link></td>
                      <td className="py-3 px-4 text-[var(--text-2)]">{formatDate(r.delivery_date)}</td>
                      <td className="py-3 px-4 text-[var(--text-2)]">{r.driver_name}</td>
                      <td className="py-3 px-4 text-[var(--text-2)]">{r.customer_name}</td>
                      <td className="py-3 px-4 text-xs text-[var(--text-3)]">{r.from} → {r.to}</td>
                      <td className="py-3 px-4 text-right font-mono font-semibold" style={{ color: "var(--header)" }}>{Number(r.amount).toFixed(2)}</td>
                      <td className="py-3 px-4"><StatusBadge s={r.payout_status} /></td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-1">
                          {(r.payout_status || "open") === "open" && (
                            <button
                              type="button"
                              onClick={() => setPayoutStatus(r.id, "invoiced")}
                              data-testid={`payout-mark-invoiced-${r.id}`}
                              title="Fahrer-Rechnung erhalten / erfasst"
                              className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#D4AF37]/50 text-[#7A5A0E] hover:bg-[#D4AF37]/10 rounded-sm inline-flex items-center gap-1"
                            >
                              <Receipt size={11} /> Fakturiert
                            </button>
                          )}
                          {r.payout_status === "invoiced" && (
                            <button
                              type="button"
                              onClick={() => setPayoutStatus(r.id, "paid")}
                              data-testid={`payout-mark-paid-${r.id}`}
                              title="Als bezahlt markieren"
                              className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-500/40 text-emerald-800 hover:bg-emerald-500/10 rounded-sm inline-flex items-center gap-1"
                            >
                              <CircleDollarSign size={11} /> Bezahlt
                            </button>
                          )}
                          {r.payout_status === "paid" && (
                            <button
                              type="button"
                              onClick={() => setPayoutStatus(r.id, "open")}
                              data-testid={`payout-reset-${r.id}`}
                              title="Status zurücksetzen"
                              className="text-[10px] uppercase tracking-wider px-2 py-1 text-[var(--text-3)] hover:text-red-700 rounded-sm inline-flex items-center gap-1"
                            >
                              <Circle size={11} /> Reset
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "customer" && (<>
        {/* Filters */}
        <div className="card p-4 flex flex-wrap gap-3 items-end">
          <div className="flex-1 min-w-[180px]">
            <label className="label-eyebrow block mb-2 flex items-center gap-2"><Filter size={11} /> Kunde</label>
            <select
              data-testid="billing-filter-customer"
              className="input-base"
              value={filters.customer_id}
              onChange={(e) => setFilters({ ...filters, customer_id: e.target.value })}
            >
              <option value="">Alle Kunden</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="min-w-[140px]">
            <label className="label-eyebrow block mb-2">Monat</label>
            <select
              data-testid="billing-filter-month"
              className="input-base"
              value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value })}
            >
              <option value="">Alle</option>
              {months.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="min-w-[140px]">
            <label className="label-eyebrow block mb-2">Status</label>
            <select
              data-testid="billing-filter-status"
              className="input-base"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">Alle</option>
              <option value="erstellt">Erstellt</option>
              <option value="geplant">Geplant</option>
              <option value="zwischenlager">Zwischenlager</option>
              <option value="unterwegs">Unterwegs</option>
              <option value="zugestellt">Zugestellt</option>
              <option value="storniert">Storniert</option>
            </select>
          </div>
          <div className="min-w-[160px]">
            <label className="label-eyebrow block mb-2 flex items-center gap-2"><Receipt size={11} /> Rechnungsstatus</label>
            <select
              data-testid="billing-filter-invoice-status"
              className="input-base"
              value={filters.invoice_status}
              onChange={(e) => setFilters({ ...filters, invoice_status: e.target.value })}
            >
              <option value="">Alle</option>
              <option value="open">Offen</option>
              <option value="invoiced">Fakturiert</option>
              <option value="paid">Bezahlt</option>
            </select>
          </div>
        </div>

        {error && <div className="border border-red-500/30 bg-red-500/5 text-red-700 text-sm px-3 py-2 rounded-sm">{error}</div>}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="card p-5">
            <div className="text-xs uppercase tracking-wider text-[var(--text-3)] flex items-center gap-2"><TrendingUp size={12} /> Umsatz</div>
            <div className="text-2xl md:text-3xl font-semibold mt-2" data-testid="billing-kpi-revenue">{chf(data.summary.total_revenue)}</div>
          </div>
          <div className="card p-5">
            <div className="text-xs uppercase tracking-wider text-[var(--text-3)] flex items-center gap-2"><Receipt size={12} /> Aufträge</div>
            <div className="text-2xl md:text-3xl font-semibold mt-2" data-testid="billing-kpi-orders">{data.summary.order_count}</div>
          </div>
          <div className="card p-5">
            <div className="text-xs uppercase tracking-wider text-[var(--text-3)]">Ø pro Auftrag</div>
            <div className="text-2xl md:text-3xl font-semibold mt-2" data-testid="billing-kpi-avg">
              {chf(data.summary.order_count ? data.summary.total_revenue / data.summary.order_count : 0)}
            </div>
          </div>
          <div className="card p-5">
            <div className="text-xs uppercase tracking-wider text-[var(--text-3)] flex items-center gap-2"><UsersIcon size={12} /> Top Kunde</div>
            <div className="text-sm font-semibold mt-2 truncate" data-testid="billing-kpi-top">
              {data.summary.by_customer[0]?.customer_name || "—"}
            </div>
            <div className="text-xs text-[var(--text-2)] mt-1">
              {data.summary.by_customer[0] ? chf(data.summary.by_customer[0].total) : ""}
            </div>
          </div>
        </div>

        {/* Monatlicher Verlauf */}
        <div className="card p-5">
          <div className="label-eyebrow mb-3">Umsatzverlauf · letzte 18 Monate</div>
          {data.summary.by_month.length === 0 ? (
            <div className="text-sm text-[var(--text-3)] py-6 text-center">Keine Daten für gewählten Filter.</div>
          ) : (
            <div className="flex items-end gap-2 h-32">
              {data.summary.by_month.map(m => (
                <div key={m.month} className="flex-1 flex flex-col items-center justify-end group">
                  <div
                    className="w-full rounded-t-sm"
                    style={{
                      background: "var(--header)",
                      height: `${(m.total / monthMax) * 100}%`,
                      minHeight: "4px",
                    }}
                    title={`${m.month}: ${chf(m.total)}`}
                  />
                  <div className="text-[9px] uppercase tracking-wider text-[var(--text-3)] mt-1.5">{m.month.slice(5)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Aufträge Tabelle */}
        <div className="card overflow-x-auto">
          <table className="w-full text-sm min-w-[960px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                <th className="text-left py-3 px-4">Auftrag</th>
                <th className="text-left py-3 px-4">Kunde</th>
                <th className="text-left py-3 px-4">Datum</th>
                <th className="text-left py-3 px-4">Modell</th>
                <th className="text-right py-3 px-4">Paletten</th>
                <th className="text-right py-3 px-4">Total</th>
                <th className="text-left py-3 px-4">Rechnung</th>
                <th className="text-right py-3 px-4">Aktion</th>
              </tr>
            </thead>
            <tbody data-testid="billing-orders-table">
              {loading && (<tr><td colSpan={8} className="text-center py-10 text-[var(--text-3)]">Lade…</td></tr>)}
              {!loading && data.orders.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-[var(--text-3)]">Keine Aufträge im gewählten Zeitraum.</td></tr>
              )}
              {!loading && data.orders.map(o => (
                <tr key={o.id} className="border-b border-[var(--border)]" data-testid={`billing-row-${o.id}`}>
                  <td className="py-3 px-4 font-mono text-[var(--text)]">
                    <Link to={`/admin/orders/${o.id}`} className="hover:text-[#D4AF37]">{o.order_number}</Link>
                  </td>
                  <td className="py-3 px-4 text-[var(--text-2)]">{o.customer_name || "—"}</td>
                  <td className="py-3 px-4 text-[var(--text-2)]">{formatDate(o.delivery_date)}</td>
                  <td className="py-3 px-4">
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border" style={{
                      borderColor: o.model_used === "flat" ? "rgba(212,175,55,0.5)" : "rgba(98,183,72,0.5)",
                      color: o.model_used === "flat" ? "#7A5A0E" : "#15803D",
                      background: o.model_used === "flat" ? "rgba(212,175,55,0.10)" : "rgba(98,183,72,0.10)",
                    }}>
                      {o.model_used === "flat" ? "Pauschal" : o.model_used === "tier" ? "Staffel" : "—"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right text-[var(--text-2)] font-mono">{o.pallet_count}</td>
                  <td className="py-3 px-4 text-right font-mono font-semibold" style={{ color: "var(--header)" }}>
                    {Number(o.total).toFixed(2)}
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex flex-col items-start gap-0.5">
                      <StatusBadge s={o.invoice_status} />
                      {o.invoice_number && <span className="text-[10px] text-[var(--text-3)] font-mono">Nr. {o.invoice_number}</span>}
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-end gap-1">
                      {o.invoice_status === "open" && (
                        <button
                          type="button"
                          onClick={() => setInvoiceStatus(o.id, "invoiced")}
                          data-testid={`billing-mark-invoiced-${o.id}`}
                          title="Rechnung erstellt (extern)"
                          className="text-[10px] uppercase tracking-wider px-2 py-1 border border-[#D4AF37]/50 text-[#7A5A0E] hover:bg-[#D4AF37]/10 rounded-sm inline-flex items-center gap-1"
                        >
                          <Receipt size={11} /> Fakturiert
                        </button>
                      )}
                      {o.invoice_status === "invoiced" && (
                        <button
                          type="button"
                          onClick={() => setInvoiceStatus(o.id, "paid")}
                          data-testid={`billing-mark-paid-${o.id}`}
                          title="Als bezahlt markieren"
                          className="text-[10px] uppercase tracking-wider px-2 py-1 border border-emerald-500/40 text-emerald-800 hover:bg-emerald-500/10 rounded-sm inline-flex items-center gap-1"
                        >
                          <CheckCircle2 size={11} /> Bezahlt
                        </button>
                      )}
                      {o.invoice_status === "paid" && (
                        <button
                          type="button"
                          onClick={() => setInvoiceStatus(o.id, "open")}
                          data-testid={`billing-reset-${o.id}`}
                          title="Status zurücksetzen"
                          className="text-[10px] uppercase tracking-wider px-2 py-1 text-[var(--text-3)] hover:text-red-700 rounded-sm inline-flex items-center gap-1"
                        >
                          <Circle size={11} /> Reset
                        </button>
                      )}
                      <button
                        onClick={() => downloadSingle(o.id, o.order_number)}
                        data-testid={`billing-invoice-${o.id}`}
                        title="Rechnung PDF öffnen"
                        className="text-[var(--text-3)] hover:text-[#D4AF37] inline-flex items-center gap-1 text-xs uppercase tracking-wider px-2 py-1"
                      >
                        <FileText size={13} /> PDF
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>)}
      </div>
    </Layout>
  );
}
